import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { User, Lock, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../utils/cn';

export default function Settings() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [autopilotMsg, setAutopilotMsg] = useState(null);
  const autopilotEnabled = user?.autopilot_globally_enabled ?? true;

  const handleAutopilotToggle = async () => {
    try {
      const response = await api.put('/auth/update-profile', { autopilot_globally_enabled: !autopilotEnabled });
      setUser(response.data);
      setAutopilotMsg(null);
    } catch (err) {
      setAutopilotMsg({ type: 'error', text: err.response?.data?.error || t('settings.update_failed') });
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put('/auth/update-profile', { name, email });
      setUser(response.data);
      setProfileMsg({ type: 'success', text: t('settings.profile_updated') });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || t('settings.update_failed') });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordMsg({ type: 'success', text: t('settings.password_changed') });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || t('settings.password_change_failed') });
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.title')}</h1>

      <div className="space-y-6 max-w-2xl">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <User className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.profile')}</h2>
          </div>
          <form onSubmit={handleProfileUpdate} className="px-6 py-4 space-y-4">
            {profileMsg && (
              <Message type={profileMsg.type} text={profileMsg.text} />
            )}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t('settings.name')}</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="settings-email" className="block text-sm font-medium text-gray-700 mb-1">{t('settings.email')}</label>
              <input
                id="settings-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              {t('settings.save_changes')}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <Zap className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.autopilot_title')}</h2>
          </div>
          <div className="px-6 py-4 space-y-3">
            {autopilotMsg && <Message type={autopilotMsg.type} text={autopilotMsg.text} />}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {autopilotEnabled ? t('settings.autopilot_active') : t('settings.autopilot_paused')}
                </p>
                <p className="text-sm text-gray-500">{t('settings.autopilot_hint')}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autopilotEnabled}
                onClick={handleAutopilotToggle}
                className={cn(
                  'relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors',
                  autopilotEnabled ? 'bg-green-600' : 'bg-gray-300'
                )}
              >
                <span
                  className={cn(
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                    autopilotEnabled ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <Lock className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.change_password')}</h2>
          </div>
          <form onSubmit={handlePasswordChange} className="px-6 py-4 space-y-4">
            {passwordMsg && (
              <Message type={passwordMsg.type} text={passwordMsg.text} />
            )}
            <div>
              <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">{t('settings.current_password')}</label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">{t('settings.new_password')}</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              {t('settings.change_password')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Message({ type, text }) {
  const Icon = type === 'success' ? CheckCircle : AlertCircle;
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
      type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
    }`}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}
